/**
 * DI-2-9 — the Flights step's server reads and writes (`intake/v1` 1.5.0):
 *
 *   GET   /data-acquisitions/{id}/log-candidates   listLogCandidates  (DI-2-5)
 *   POST  /data-acquisitions/{id}/match            requestMatch       (DI-2-5)
 *   GET   /data-acquisitions/{id}/intake           getIntakeStatus    (DI-2-9)
 *   GET   /data-acquisitions/{id}/intake/points    listIntakePoints   (DI-2-9)
 *   PATCH /data-acquisitions/{id}/flights/{fid}    patchIntakeFlight  (DI-2-9)
 *   GET   /intakes                                 listIntakes        (DI-2-15)
 *   GET   /data-acquisitions/{id}/intake/blobs     listIntakeBlobs    (DI-3-S9)
 *
 * Every read is tenant-scoped and `enabled`-gated on tenant readiness
 * (memory `reference_tenant_scoped_query_enabled_gate`). The status is polled
 * (DI-3-D, `statusPollInterval`): every 2 s while any server stage runs, the
 * server has work queued (`indexing` / `matching`) or the runner still has a
 * browser phase pending for the set; every 30 s while it waits for a log
 * (`awaiting.subscribed`, N3 — never a dead end); off otherwise. A status
 * without `stages` (an older gateway) keeps DI-2-9's rule: 2 s while
 * `matching`. The board never asks for `POST …/match` (N5). The read is
 * cheap by design (DI-3-A AC-8: 12 ms median). The points are read whole (all pages,
 * 1 000 per page) and re-read whenever the status leaves `matching`, because
 * a finished run is what changes them.
 */
import { useEffect, useMemo, useRef } from "react";
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useAuthStore } from "@aeriosiq/auth-client";
import { intakeFetch, isIntakeFetchError } from "./intakeFetch.js";
import { rememberSetTenant } from "./setTenants.js";
import { intakeKeys } from "./keys.js";
import {
  ownerSlugOf,
  sharedWorkflows,
  useWorkflows,
  type Workflow,
} from "../../workflows/api/workflows.js";
import { sharedOwnersOf, type SessionRecord } from "../session/sessionStore.js";
import type {
  FlightPatch,
  IntakeBlobsPage,
  IntakeFlight,
  IntakeListItem,
  IntakeListPage,
  IntakePoint,
  IntakePointsPage,
  IntakeStatus,
  LogCandidateList,
  MatchAccepted,
} from "./intakeTypes.js";

export const STATUS_POLL_MS = 2_000;
/**
 * DI-3-S24 (item 4) — an OPEN card whose set re-runs (`matching`) reads its
 * status every second, so the new numbers follow the run by ≤ 1 s. Only the
 * open card's own observer asks for it (`useIntakeStatus(…, { openCard })`);
 * the board's reads keep `STATUS_POLL_MS`, and the open card falls back to
 * the board's rule once the run has ended.
 */
export const OPEN_CARD_POLL_MS = 1_000;
/** DI-3-D (N3) — a set waiting for its flight log is subscribed: polled slowly. */
export const AWAITING_POLL_MS = 30_000;
export const POINTS_PAGE_SIZE = 1_000;

function da(daId: string): string {
  return `/data-acquisitions/${encodeURIComponent(daId)}`;
}

function useTenantReady(): boolean {
  return useAuthStore((s) => Boolean(s.tenant?.tenantSlug));
}

export function fetchIntakeStatus(
  daId: string,
  opts: { includeDisabled?: boolean } = {},
): Promise<IntakeStatus> {
  return intakeFetch<IntakeStatus>(
    `${da(daId)}/intake${opts.includeDisabled ? "?includeDisabled=true" : ""}`,
  );
}

/**
 * DI-3-D — how often one acquisition's status is read (story §2.1; locked
 * poll-every-running-set). `localPending` = the runner still has a browser
 * phase for the set (create, hash, metadata; DI-3-F the upload).
 */
export function statusPollInterval(
  s: IntakeStatus | undefined,
  localPending = false,
): number | false {
  if (!s) return localPending ? STATUS_POLL_MS : false;
  if (!s.stages) {
    // an older gateway (no status v2): DI-2-9's rule
    return s.intakeState === "matching" || localPending
      ? STATUS_POLL_MS
      : false;
  }
  if (s.stages.some((st) => st.state === "running")) return STATUS_POLL_MS;
  if (s.intakeState === "indexing" || s.intakeState === "matching")
    return STATUS_POLL_MS;
  if (localPending) return STATUS_POLL_MS;
  if (s.awaiting?.subscribed || s.intakeState === "awaiting_logs")
    return AWAITING_POLL_MS;
  return false;
}

/**
 * DI-3-P6 — has the set LEFT the intake? The server's answer, ONE definition
 * for the board, the verify list, `?open=` and the session's end
 * (`runner/sessionEnd.ts`):
 *
 *   · the status reads `intakeState: null` — finalized by Approve, or
 *     promoted by the ingest after an "after verification" upload (the
 *     gateway clears the column on both paths);
 *   · the status read answers 404 — the acquisition is gone (withdrawn: it
 *     is REJECTED and soft-deleted, the gateway reads only undeleted rows).
 *
 * Anything else is NOT left: a Move keeps the acquisition (its status reads
 * the new run's state), a withheld partner set reads `withheld_complete` until
 * Approve finalizes it, `approved_pending_upload` keeps the state until the
 * ingest promotes the set, and any other failed read (a 5xx, the network) says
 * nothing about the set — a transient error must never end a session.
 * react-query keeps the last good `data` beside an `error`, so the 404 wins
 * over a stale status.
 */
export function leftTheIntake(
  status: IntakeStatus | null | undefined,
  error?: unknown,
): boolean {
  return goneFromIntake(error) || status?.intakeState === null;
}

/** Was the set's status read answered 404 (the acquisition is gone — withdrawn)? */
export function goneFromIntake(error: unknown): boolean {
  return isIntakeFetchError(error) && error.status === 404;
}

/**
 * DI-3-S24 (item 4) — the open card's rule: every `OPEN_CARD_POLL_MS` while
 * the set is `matching` (a re-run the pilot is waiting on), else the board's
 * rule (`statusPollInterval`).
 */
export function openCardPollInterval(
  s: IntakeStatus | undefined,
  localPending = false,
): number | false {
  if (s?.intakeState === "matching") return OPEN_CARD_POLL_MS;
  return statusPollInterval(s, localPending);
}

/** The status query's options — one factory for the Flights step and the board. */
export function statusQueryOptions(
  daId: string,
  enabled: boolean,
  localPending = false,
  openCard = false,
) {
  const rule = openCard ? openCardPollInterval : statusPollInterval;
  return {
    queryKey: intakeKeys.status(daId),
    queryFn: () => fetchIntakeStatus(daId),
    enabled: enabled && daId.length > 0,
    retry: false,
    // A 404 means the set left the intake (withdrawn — e.g. by the DI-3-S40
    // stale-set sweep): stop. react-query keeps the last good `data` beside
    // the error, and that `indexing` status alone would poll every second
    // forever (prod 2026-10-22, Wieland's withdrawn set).
    refetchInterval: (q: {
      state: { data?: IntakeStatus | undefined; error?: unknown };
    }) =>
      goneFromIntake(q.state.error) ? false : rule(q.state.data, localPending),
  } as const;
}

/**
 * One acquisition's status. `openCard` (DI-3-S24): this observer is an OPEN
 * card's — it polls every second while the set re-runs. TanStack Query runs
 * each observer's interval on its own, so the shared cache entry is read at
 * the fastest rate any mounted observer asks for: the board's 2 s observer
 * is unchanged, and the 1 s ends with the open card (or with the run).
 */
export function useIntakeStatus(
  daId: string | null | undefined,
  opts: { localPending?: boolean; openCard?: boolean } = {},
): UseQueryResult<IntakeStatus> {
  const tenantReady = useTenantReady();
  return useQuery<IntakeStatus>(
    statusQueryOptions(
      daId ?? "",
      tenantReady,
      opts.localPending ?? false,
      opts.openCard ?? false,
    ),
  );
}

/**
 * DI-3-6 (intake/v1 1.24.0) — the status read WITH the absorbed flights
 * (`includeDisabled=true`; the default read hides them). Read only while the
 * Flights step's "Include switched-off flights" is on; polled by the same rule.
 */
export function useIntakeStatusWithAbsorbed(
  daId: string | null | undefined,
  on: boolean,
): UseQueryResult<IntakeStatus> {
  const tenantReady = useTenantReady();
  const id = daId ?? "";
  return useQuery<IntakeStatus>({
    queryKey: intakeKeys.statusWithAbsorbed(id),
    queryFn: () => fetchIntakeStatus(id, { includeDisabled: true }),
    enabled: on && tenantReady && id.length > 0,
    retry: false,
    refetchInterval: (q) =>
      on && !goneFromIntake(q.state.error)
        ? statusPollInterval(q.state.data)
        : false,
  });
}

/**
 * DI-3-D — the board: every set's status, each polled by its own rule. The
 * same query keys as `useIntakeStatus`, so a card and the Flights step share
 * one cache entry.
 */
export function useIntakeStatuses(
  sets: readonly { daId: string | null; localPending: boolean }[],
): UseQueryResult<IntakeStatus>[] {
  const tenantReady = useTenantReady();
  return useQueries({
    queries: sets.map((s) =>
      statusQueryOptions(s.daId ?? "", tenantReady, s.localPending),
    ),
  });
}

/** Every page of the acquisition's points, in the server's order. */
export async function fetchAllIntakePoints(
  daId: string,
  pageSize: number = POINTS_PAGE_SIZE,
): Promise<IntakePoint[]> {
  const items: IntakePoint[] = [];
  for (let page = 1; ; page++) {
    const r = await intakeFetch<IntakePointsPage>(
      `${da(daId)}/intake/points?page=${String(page)}&pageSize=${String(pageSize)}`,
    );
    items.push(...r.items);
    if (r.items.length === 0 || items.length >= r.total) return items;
  }
}

/** DI-3-S9 — `listIntakeBlobs`' page size: the gateway's maximum (one bounded query per 1 000). */
export const BLOBS_PAGE_SIZE = 1_000;

/** DI-3-S9 — the states `listIntakeBlobs` filters on (`images.blob_state`). */
export type BlobState = "present" | "withheld" | "missing";

/**
 * DI-3-S9 (smoke F8) — the file names of the acquisition in one blob state
 * (`listIntakeBlobs?state=…`, intake/v1 1.20.0): every page, one call per
 * 1 000, in the server's order (by name). Names only, never bytes (DI-D11).
 */
export async function fetchBlobNames(
  daId: string,
  state: BlobState,
  pageSize: number = BLOBS_PAGE_SIZE,
): Promise<Set<string>> {
  const names = new Set<string>();
  let seen = 0;
  for (let page = 1; ; page++) {
    const r = await intakeFetch<IntakeBlobsPage>(
      `${da(daId)}/intake/blobs?state=${state}&page=${String(page)}&pageSize=${String(pageSize)}`,
    );
    for (const b of r.items) names.add(b.imageFilename);
    seen += r.items.length;
    if (r.items.length === 0 || seen >= r.total) return names;
  }
}

/**
 * DI-3-S9 — the originals the server KNOWS it lacks (`state=missing`: an
 * ingest verified the blob absent or wrong). The resume's reconcile
 * (`upload/ledger.ts:reconcile`) drops exactly those ledger entries; a blob
 * no ingest has checked yet (`unknown`) is not in it, so the ledger's skip of
 * what this browser sent stays (DI-3-S4-FE). The SAS is write-only (`cw`):
 * the browser cannot ask the blob store itself.
 */
export function fetchMissingBlobs(daId: string): Promise<Set<string>> {
  return fetchBlobNames(daId, "missing");
}

export function useIntakePoints(
  daId: string | null | undefined,
  enabled = true,
): UseQueryResult<IntakePoint[]> {
  const tenantReady = useTenantReady();
  return useQuery<IntakePoint[]>({
    queryKey: intakeKeys.points(daId ?? ""),
    queryFn: () => fetchAllIntakePoints(daId ?? ""),
    enabled: tenantReady && Boolean(daId) && enabled,
    retry: false,
  });
}

/**
 * Re-reads the points once the acquisition leaves `matching` (a run finished:
 * the component ids and the placement may have changed).
 */
export function useRefreshPointsAfterRun(
  daId: string | null | undefined,
  intakeState: string | null | undefined,
): void {
  const qc = useQueryClient();
  const prev = useRef(intakeState);
  useEffect(() => {
    if (prev.current === "matching" && intakeState !== "matching" && daId) {
      void qc.invalidateQueries({ queryKey: intakeKeys.points(daId) });
    }
    prev.current = intakeState;
  }, [daId, intakeState, qc]);
}

/**
 * DI-4-2 (intake/v1 1.33.0, DI-D15 c2 edge o) — the read options of
 * `listLogCandidates`. `includeNotRelevant: true` also considers the logs the
 * pilot answered "not relevant" on the RC, for THIS read (the log row never
 * changes); default `false` = the standing exclusion.
 */
export interface LogCandidatesOptions {
  includeNotRelevant?: boolean;
}

/** The candidate list's URL; the flag only when on (the default read is unchanged). */
export function logCandidatesPath(
  daId: string,
  opts: LogCandidatesOptions = {},
): string {
  return opts.includeNotRelevant === true
    ? `${da(daId)}/log-candidates?include_not_relevant=true`
    : `${da(daId)}/log-candidates`;
}

export function useLogCandidates(
  daId: string | null | undefined,
  opts: LogCandidatesOptions = {},
): UseQueryResult<LogCandidateList> {
  const tenantReady = useTenantReady();
  const includeNotRelevant = opts.includeNotRelevant === true;
  return useQuery<LogCandidateList>({
    queryKey: intakeKeys.candidates(daId ?? "", { includeNotRelevant }),
    queryFn: () =>
      intakeFetch<LogCandidateList>(
        logCandidatesPath(daId ?? "", { includeNotRelevant }),
      ),
    enabled: tenantReady && Boolean(daId),
    retry: false,
  });
}

/**
 * The body of `POST …/match` (`MatchRequest`). Empty = every both-pass
 * candidate. DI-4-2 — `includeNotRelevant` with `logIds` names a log the
 * pilot answered "not relevant": named, never swept (the server never adds
 * such a log to "every candidate").
 */
export interface MatchBody {
  logIds?: string[];
  includeNotRelevant?: boolean;
  /**
   * DI-4-4 / DI-4-4b (intake/v1 1.36.0) — the pilot's recorded force: one
   * entry per named log with the gate acknowledged; `logIds` the same set.
   */
  overrides?: { logId: string; acknowledgedGate: "position" | "time" }[];
}

/** POST …/match — the caller reads 202 / 409 / 422 from the mutation. */
export function useRequestMatch(daId: string) {
  const qc = useQueryClient();
  return useMutation<MatchAccepted, Error, MatchBody | undefined>({
    mutationFn: (body) =>
      intakeFetch<MatchAccepted>(`${da(daId)}/match`, {
        method: "POST",
        body: body ?? {},
      }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: intakeKeys.status(daId) });
    },
  });
}

export function patchIntakeFlight(
  daId: string,
  flightId: string,
  body: FlightPatch,
): Promise<IntakeFlight> {
  return intakeFetch<IntakeFlight>(
    `${da(daId)}/flights/${encodeURIComponent(flightId)}`,
    { method: "PATCH", body },
  );
}

/**
 * PATCH one flight. The answer replaces the flight in the cached status at
 * once (the card shows what the server stored, never a local guess); the
 * status is then re-read, because a serial edit moves the acquisition to
 * `matching`.
 */
export function usePatchFlight(daId: string) {
  const qc = useQueryClient();
  return useMutation<
    IntakeFlight,
    Error,
    { flightId: string; body: FlightPatch }
  >({
    mutationFn: ({ flightId, body }) => patchIntakeFlight(daId, flightId, body),
    onSuccess: (flight) => {
      qc.setQueryData<IntakeStatus>(intakeKeys.status(daId), (s) =>
        s
          ? {
              ...s,
              flights: s.flights.map((f) =>
                f.flightId === flight.flightId ? flight : f,
              ),
            }
          : s,
      );
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: intakeKeys.status(daId) });
    },
  });
}

/**
 * DI-2-15 — one page of the caller's intakes (`listIntakes`, 1.7.0). The query
 * string is built by `intakes/filters.ts:serverQueryString` (repeatable params
 * repeated — the contract's `explode: true`). `enabled: false` when the state
 * filter asks for local sessions only (the server has nothing to say).
 */
export function fetchIntakes(query: string): Promise<IntakeListPage> {
  return intakeFetch<IntakeListPage>(`/intakes?${query}`);
}

/**
 * M3C-04 (AC-5) — the same list read from a shared OWNER's tenant: the sets
 * this tenant's pilots created there under the owner's injection node (the
 * server's predicate hides every other set of the owner, M3C-02). Each row
 * is remembered as living in that tenant, so its later calls (status,
 * approve, …) send the owner's slug and its card shows the owner chip.
 */
export async function fetchOwnerIntakes(
  query: string,
  owner: { slug: string; name: string | null },
): Promise<IntakeListPage> {
  const page = await intakeFetch<IntakeListPage>(`/intakes?${query}`, {
    tenantSlug: owner.slug,
  });
  for (const r of page.items) rememberSetTenant(r.dataAcquisitionId, owner);
  return page;
}

/**
 * M3C-11 (lock `owners-from-the-share`, smoke row 2.6) — the owners the three
 * lists ask: the UNION of the owners this browser's sets were created under
 * (`sessionStore.ts:sharedOwnersOf`) AND the owner of every injection node
 * shared with the tenant (`useWorkflows()`'s shared rows: `ownerSlugOf` /
 * `ownerTenantName`). So every user of a grantee tenant asks the owner, even
 * with an EMPTY session store (another browser, another pilot); what comes
 * back is limited by the server (`created_by_tenant_id` = the caller's tenant,
 * M3C-02), and "mine" keeps meaning "created by me" (the query, unchanged).
 * One entry per owner slug; a name from either source.
 */
export function sharedOwnersFor(
  records: readonly SessionRecord[],
  workflows: readonly Workflow[] | undefined,
): { slug: string; name: string | null }[] {
  const out = new Map<string, string | null>();
  for (const o of sharedOwnersOf(records)) out.set(o.slug, o.name);
  for (const w of sharedWorkflows(workflows)) {
    const slug = ownerSlugOf(w);
    if (!slug) continue;
    if (!out.has(slug) || out.get(slug) == null)
      out.set(slug, w.ownerTenantName ?? out.get(slug) ?? null);
  }
  return [...out].map(([slug, name]) => ({ slug, name }));
}

/** `sharedOwnersFor` over the page's session records and the tenant's workflow list. */
export function useSharedOwners(
  records: readonly SessionRecord[] | null | undefined,
): { slug: string; name: string | null }[] {
  const workflows = useWorkflows();
  return useMemo(
    () => sharedOwnersFor(records ?? [], workflows.data),
    [records, workflows.data],
  );
}

/**
 * M3C-04 (AC-5) — Board / History / Tasks: the own-tenant query stays as it
 * is, plus ONE query per shared owner (M3C-11: `useSharedOwners` — this
 * browser's sets AND the shares of `useWorkflows()`); the rows are merged by the page. No
 * other branching (DI-D62 c3: lists stay unconditional). An owner query that
 * fails (a revoked share → 403) contributes nothing.
 */
export function useSharedOwnerIntakes(
  query: string,
  owners: readonly { slug: string; name: string | null }[],
): IntakeListItem[] {
  const tenantReady = useTenantReady();
  return useQueries({
    queries: owners.map((o) => ({
      queryKey: [...intakeKeys.intakes(query), "owner", o.slug] as const,
      queryFn: () => fetchOwnerIntakes(query, o),
      enabled: tenantReady,
      retry: false,
    })),
    // memoised by TanStack: a new array only when a page changed
    combine: (results) => results.flatMap((r) => r.data?.items ?? []),
  });
}

/** Own rows first, then the owners' rows not already listed (one row per set). */
export function mergeOwnerRows<T extends { dataAcquisitionId: string }>(
  own: readonly T[],
  shared: readonly T[],
): T[] {
  const seen = new Set(own.map((r) => r.dataAcquisitionId));
  return [...own, ...shared.filter((r) => !seen.has(r.dataAcquisitionId))];
}

export function useIntakes(
  query: string,
  enabled = true,
): UseQueryResult<IntakeListPage> {
  const tenantReady = useTenantReady();
  return useQuery<IntakeListPage>({
    queryKey: intakeKeys.intakes(query),
    queryFn: () => fetchIntakes(query),
    enabled: tenantReady && enabled,
    retry: false,
    // A page stays on screen while the next one loads (no flash to empty).
    placeholderData: (prev) => prev,
  });
}

/**
 * DI-3-P8 — My Intake's open sets: the pages read so far (page 1 at mount,
 * one more per "Show more"), each its own cache entry under the list's key —
 * so an Approve's list invalidation re-reads every page shown.
 */
export function useIntakePages(
  queries: readonly string[],
): UseQueryResult<IntakeListPage>[] {
  const tenantReady = useTenantReady();
  return useQueries({
    queries: queries.map((query) => ({
      queryKey: intakeKeys.intakes(query),
      queryFn: () => fetchIntakes(query),
      enabled: tenantReady,
      retry: false,
    })),
  });
}
